import React, { useMemo } from "react"
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button
} from "@mui/material"
import AssessmentIcon from "@mui/icons-material/Assessment"
import AddIcon from "@mui/icons-material/Add"
import type { PersistedMonthlyReport } from "@/features/profit-calculators/calculator-shared/types"

const fmt = (n: number) => `₹${(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
const fmtPct = (n: number) => `${(n ?? 0).toFixed(1)}%`

interface Props {
  platform: "meesho" | "amazon"
  reports: PersistedMonthlyReport[]
  monthOptions: { monthKey: string; label: string }[]
  onGenerate: (monthKey: string) => void
  onView: (monthKey: string) => void
}

const MonthlyReportsTable: React.FC<Props> = ({ platform, reports, monthOptions, onGenerate, onView }) => {
  const reportsByMonth = useMemo(() => {
    const map = new Map<string, PersistedMonthlyReport>()
    reports.forEach(r => map.set(r.monthKey, r))
    return map
  }, [reports])

  // Only month options approved by the reports page are rendered; unexpected report documents stay hidden.
  const rows = useMemo(
    () =>
      monthOptions
        .map(option => ({ ...option, report: reportsByMonth.get(option.monthKey) ?? null }))
        .sort((a, b) => b.monthKey.localeCompare(a.monthKey)),
    [reportsByMonth, monthOptions]
  )

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Table sx={{ "& td, & th": { borderColor: "divider" } }}>
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: "text.secondary",
                bgcolor: "action.hover",
                py: 1.5
              }
            }}>
            <TableCell>Month</TableCell>
            <TableCell align="right">Net Profit</TableCell>
            <TableCell align="right">Orders</TableCell>
            <TableCell align="right">ROI</TableCell>
            <TableCell align="right">Returns %</TableCell>
            <TableCell align="right">Settlement</TableCell>
            {platform === "amazon" && <TableCell align="right">Ad Spend</TableCell>}
            <TableCell align="right">COGS</TableCell>
            <TableCell align="center">Report</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ monthKey, label, report }) => (
            <TableRow
              key={monthKey}
              hover={Boolean(report)}
              sx={{
                "&:last-child td": { border: 0 },
                ...(!report && { bgcolor: "action.hover", opacity: 0.7 })
              }}>
              <TableCell>
                <Typography variant="body2" fontWeight={700}>
                  {label}
                </Typography>
              </TableCell>
              {report ? (
                <>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color={report.netProfit < 0 ? "error.main" : "success.main"}>
                      {fmt(report.netProfit)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{report.totalOrders}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmtPct(report.roiPct)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmtPct(report.returnPct)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmt(report.totalSettlement)}</Typography>
                  </TableCell>
                  {platform === "amazon" && (
                    <TableCell align="right">
                      <Typography variant="body2">{fmt(report.totalAdsCost)}</Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Typography variant="body2">{fmt(report.totalPurchaseCost)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AssessmentIcon fontSize="small" />}
                      onClick={() => onView(monthKey)}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600
                      }}>
                      View
                    </Button>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  </TableCell>
                  {platform === "amazon" && (
                    <TableCell align="right">
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={() => onGenerate(monthKey)}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600
                      }}>
                      Generate
                    </Button>
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default MonthlyReportsTable
